import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Map, Building2, Globe, Crown } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

const ICONS = [MapPin, Map, Building2, Globe, Crown];

export default function Scaling() {
  const { t } = useLang();
  const sc = t.scaling;

  return (
    <section id="scaling" className="py-24 md:py-32 relative overflow-hidden" style={{ background: '#0F1115' }}>
      <div
        className="orb-purple absolute"
        style={{ width: 500, height: 500, top: '30%', right: '-5%', opacity: 0.08 }}
      />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-8 h-px bg-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs uppercase tracking-widest font-semibold">Scale</span>
          </div>
          <h2 
            className="text-4xl md:text-6xl font-black text-white mb-4 font-heading"
            style={{
              fontFamily: "'Sora', sans-serif",
              textShadow: '0 2px 30px rgba(0,0,0,0.5)'
            }}
          >
            {sc.title}
          </h2>
          <p className="text-[#BFC3C9] text-lg">{sc.subtitle}</p>
        </motion.div>

        {/* Timeline-style cards */}
        <div className="relative">
          {/* Connecting line */}
          <div
            className="absolute left-8 top-8 bottom-8 w-px hidden md:block"
            style={{ background: 'linear-gradient(180deg, #D4AF37 0%, #6B4EFF 100%)', opacity: 0.2 }}
          />

          <div className="space-y-4">
            {sc.items.map((item, i) => {
              const Icon = ICONS[i];
              const isLast = i === sc.items.length - 1;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                  className={`glass rounded-2xl p-6 md:p-8 flex items-center gap-6 card-lift group ${
                    isLast ? 'card-lift-purple' : ''
                  }`}
                  style={{
                    borderColor: isLast ? 'rgba(107,78,255,0.2)' : 'rgba(255,255,255,0.08)',
                    background: isLast
                      ? 'linear-gradient(135deg, rgba(107,78,255,0.06), rgba(255,255,255,0.02))'
                      : undefined,
                  }}
                  data-testid={`scaling-item-${i}`}
                >
                  {/* Number */}
                  <span
                    className="text-6xl font-black opacity-10 font-heading hidden md:block flex-shrink-0"
                    style={{
                      color: isLast ? '#9B7FFF' : '#D4AF37',
                      width: 70,
                      lineHeight: 1,
                    }}
                  >
                    {item.num}
                  </span>

                  {/* Icon */}
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110"
                    style={{
                      background: isLast ? 'rgba(107,78,255,0.1)' : 'rgba(212,175,55,0.08)',
                      border: `1px solid ${isLast ? 'rgba(107,78,255,0.25)' : 'rgba(212,175,55,0.2)'}`,
                    }}
                  >
                    <Icon size={24} style={{ color: isLast ? '#9B7FFF' : '#D4AF37' }} />
                  </div>

                  {/* Content */}
                  <div>
                    <h3 className="text-white font-bold text-xl font-heading mb-1">{item.title}</h3>
                    <p className="text-[#BFC3C9] text-sm">{item.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
